import { FC, useState } from "react"
import { IPage, PageType } from "../../models"
import { MUIIcon, ProgressIndicator } from "../../../../widgets"

import { AvatarText } from "../../../components/avatar/AvatarText"
import { PageToolbarActions } from "../../../page-actions"
import clsx from "clsx"
import { useDataContext } from "../../context"

interface IPageHeaderProps {
    page: IPage
    showTitle: boolean,
    showRefresh: boolean,
    className?: string,
    onError?: () => void
}

export const PageHeader: FC<IPageHeaderProps> = ({ page, showTitle, className, showRefresh, onError }) => {
    return (
        <div className={clsx(className, "d-flex flex-wrap gap-5")}>
            {(showTitle) && <PageTitle title={page.title || page.name} avatar={false} />}

            <HeaderToolbarActions page={page} showRefresh={showRefresh} onError={onError} />
        </div>
    )
}

const PageTitle = ({ title, avatar }: { title: string, avatar?: boolean }) => {
    return (
        <div className="flex-fill d-flex align-items-center gap-5">
            <BackButton />

            {(avatar === true) && <>
                <AvatarText
                    label={title}
                    size={2}
                    random={false}
                    className="bg-gray-700 fw-bolder" />
            </>}
            <h1 className="my-0 text-gray-700">{title}</h1>
        </div>
    )
}

const HeaderToolbarActions = ({ page, showRefresh, onError }: {
    page: IPage,
    onError?: (message: string) => void,
    showRefresh: boolean
}) => {

    const getToolbarActions = () => {
        if (!page.actions || page.actions.length === 0)
            return <></>

        if (page.type === PageType.Root)
            return <></>

        return <>
            <PageToolbarActions className="mb-2" actions={page.actions || []} />
        </>
    }

    return <div className="d-flex gap-2 align-items-center">
        {getToolbarActions()}
        {(showRefresh) && <RefreshButton page={page} onError={onError} />}
    </div>
}

const RefreshButton = ({ page, onError }: {
    page: IPage,
    onError?: (message: string) => void
}) => {
    const { refetch } = useDataContext()
    const [refreshing, setRefreshing] = useState<boolean>(false)

    if (page.type === PageType.CardPart || page.type === PageType.ListPart)
        return <></>

    const handleRefresh = () => {
        if (!refetch)
            return;

        setRefreshing(true)
        refetch()
            .catch(err => {
                if (!onError)
                    return Promise.reject(err)

                const message = err.data.message || 'An error occurred while processing your request. Please try again later.'
                onError(message)
            })
            .finally(() => setRefreshing(false))
    }

    return <>
        <button type="button"
            disabled={refreshing}
            onClick={handleRefresh}
            title="Refresh the data"
            className="btn bg-transparent btn-sm py-2 px-0">
            {(refreshing) ? <>
                <ProgressIndicator show={refreshing} />
            </> : <>
                <MUIIcon iconName="RefreshOutlined" className="me-1" />
            </>}
        </button>
    </>
}


const BackButton = () => {
    const handleBack = () => {
        window.history.back()
    }

    return <>
        <button type="button"
            onClick={handleBack}
            title="back"
            className="btn btn-secondary btn-sm py-2 px-0 rounded-circle w-30px h-30px d-flex align-items-center justify-content-center">
            <MUIIcon iconName="ArrowBackIosNewOutlined" className="me-1" />
        </button>
    </>
}
